<template>
  <div
    v-if="isVisible"
    ref="bannerEl"
    class="core-cookie-banner d-flex align-center justify-space-between px-4 py-2 bg-primary"
    :class="isMobile ? 'core-cookie-banner_mobile' : 'rounded'"
    data-qa="cookie-banner"
    data-nosnippet
  >
    <div class="text-body-2 text--text">
      Мы используем
      <a
        :href="policyHref"
        target="_blank"
        rel="noopener noreferrer"
        class="primary--text text-decoration-none"
        data-qa="cookie-banner-link"
      >
        {{ isRussia ? 'cookie' : 'cookie,' }}
      </a>
      {{ isRussia
        ? 'и Яндекс.Метрику, чтобы сайт работал лучше'
        : 'чтобы сайт работал лучше' }}
    </div>

    <v-btn
      color="primary"
      depressed
      class="ml-4 flex-shrink-0"
      data-qa="cookie-banner-button"
      @click="onAccept"
    >
      <span class="text-button">
        Хорошо
      </span>
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue'
import Cookies from 'js-cookie'
import { useContextStore } from 'src/store'

const COOKIE_NAME = 'is-cookie-banner-visible'

const { context } = useContextStore()
const isMobile = context.flavour === 'mobile'
const isRussia = Boolean(context.request.country.is_russia)
const staticPath = context.global.static.src

const bannerEl = ref<HTMLElement | null>(null)
const isVisible = ref(false)

const policyHref = computed(() => (
  isRussia
    ? `${staticPath}_v1/common/documents/pdf/cookie_policy_with_ya_metrics_2026.pdf`
    : `${staticPath}_v1/common/documents/pdf/cookie_policy_2026_cis.pdf`
))

const setBannerHeight = (height: number) => {
  if (!process.env.IS_CLIENT) {
    return
  }

  document.documentElement.style.setProperty('--cookie-banner-height', `${height}px`)
}

const updateBannerHeight = () => {
  const height = isVisible.value && bannerEl.value
    ? bannerEl.value.clientHeight
    : 0

  setBannerHeight(height)

  return height
}

const onResize = () => {
  const height = updateBannerHeight()

  if (height === 0) {
    window.removeEventListener('resize', onResize)
  }
}

const syncVisibility = () => {
  try {
    if (!navigator.cookieEnabled) {
      isVisible.value = true
      nextTick(updateBannerHeight)
      return
    }

    isVisible.value = !Cookies.get(COOKIE_NAME)
    nextTick(updateBannerHeight)
  } catch {
    setBannerHeight(0)
  }
}

const onAccept = () => {
  try {
    if (!navigator.cookieEnabled) {
      return
    }

    isVisible.value = false
    setBannerHeight(0)
    Cookies.set(COOKIE_NAME, String(true), {
      expires: 365,
      sameSite: 'lax',
    })

    if (isMobile) {
      window.removeEventListener('resize', onResize)
    }
  } catch { /* empty */ }
}

onMounted(() => {
  syncVisibility()

  if (isMobile) {
    window.addEventListener('resize', onResize)
  }
})

onBeforeUnmount(() => {
  if (!process.env.IS_CLIENT) {
    return
  }

  if (isMobile) {
    window.removeEventListener('resize', onResize)
  }

  setBannerHeight(0)
})
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.core-cookie-banner {
  z-index: 5;
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);

  @include elevation-6;

  &_mobile {
    position: static;
    left: auto;
    bottom: auto;
    transform: none;
    box-shadow: none;
  }
}
</style>
